import { __, sprintf } from '@wordpress/i18n';
import { Accordion, Badge, Button, Text } from '@bsf/force-ui';
import { Check, ChevronRight, Video } from 'lucide-react';
import { ActionItemsSkeleton, ActionItemStep } from './action-items';
import useStore, { actionTypes } from '@/store';

const getActionCTAText = ( type ) => {
	switch ( type ) {
		case 'video':
			return __( 'Play Video', 'getting-started' );
		case 'link':
			return __( 'Learn More', 'getting-started' );
	}

	return __( 'Learn how', 'getting-started' );
};

const ActionItems = ( {
	category = {},
	items = [],
	defaultValue,
	isLoading = false,
} ) => {
	const { dispatch } = useStore();

	const handleActionCTAClick = ( item ) => {
		const { type } = item?.cta || {};

		dispatch( {
			type:
				type === 'video'
					? actionTypes.SET_VIDEO_MODAL_ITEM
					: actionTypes.SET_LEARN_MORE_DIALOG_ITEM,
			payload: {
				open: true,
				item: item?.cta || {},
			},
		} );
	};

	// Bail early if no items and not loading.
	if ( ! isLoading && ! items?.length ) {
		return;
	}

	return (
		<div className="p-4 sm:p-6 bg-background-primary flex flex-col gap-4 sm:gap-5 border-0.5 border-solid border-border-subtle rounded-xl shadow-sm [&>div:space-y-5]">
			{ isLoading && <ActionItemsSkeleton /> }

			{ category?.name && (
				<Text size={ 16 } weight={ 600 }>
					{ category?.name }
				</Text>
			) }

			{ ! isLoading && (
				<Accordion
					type="boxed"
					autoClose={ true }
					defaultValue={ defaultValue }
				>
					{ items?.map( ( item ) => {
						const totalStepsCount = item?.steps?.length;
						const completedStepsCount = item?.steps?.filter(
							( step ) => step?.completed
						).length;
						const isCompleted =
							totalStepsCount === completedStepsCount;

						const getBadgeColor = () => {
							if ( isCompleted ) {
								return 'green';
							}
							if ( completedStepsCount > 0 ) {
								return 'blue';
							}
							return 'gray';
						};

						return (
							<Accordion.Item
								className="border-0.5 [&:hover>h3]:bg-transparent rounded-lg overflow-hidden [&:has([aria-expanded='true'])]:border-border-strong [&:has([aria-expanded='true'])]:shadow-lg transition-all duration-200 ease-in-out"
								key={ item.id }
								value={ item.id }
							>
								<Accordion.Trigger className="group p-3 sm:p-4 hover:bg-transparent [&>svg]:hidden [&>div]:flex-grow [&[aria-expanded='true']_.gs-chevron-right]:rotate-90 [&[aria-expanded='true']_.gs-play-video]:flex">
									<div className="flex items-center gap-2 sm:gap-3 flex-1">
										<ChevronRight
											className="gs-chevron-right transition-transform duration-200 ease-in-out"
											size={ 20 }
										/>

										<div className="flex-1 text-left">
											<Text
												size={ 14 }
												className="sm:text-base"
												weight={ 700 }
											>
												{ item.title }
											</Text>
										</div>

										{ item?.cta && (
											<Button
												className="gs-play-video hidden transition-all outline-none hover:bg-transparent underline-offset-3 hover:underline hover:text-gs-primary focus:ring-0 focus:ring-offset-0"
												size="xs"
												variant="ghost"
												onClick={ ( event ) => {
													event.stopPropagation();
													handleActionCTAClick(
														item
													);
												} }
												icon={
													item?.cta?.type ===
														'video' && (
														<Video
															size={ 16 }
															strokeWidth={ 1.25 }
														/>
													)
												}
											>
												<span className="hidden sm:inline">
													{ item?.cta?.label ||
														getActionCTAText(
															item?.cta?.type
														) }
												</span>
											</Button>
										) }

										<Badge
											className="relative overflow-hidden w-14 sm:w-[62px] text-xs"
											label={
												<>
													<span className="sr-only">
														{ sprintf(
															// translators: %1$d is the number of completed steps, %2$d is the total number of steps.
															__(
																'%1$d of %2$d steps completed',
																'getting-started'
															),
															completedStepsCount,
															totalStepsCount
														) }
													</span>

													<span className="flex items-center">
														{ isCompleted && (
															<Check
																size={ 12 }
															/>
														) }
														<span className="px-1 relative z-10">
															{
																completedStepsCount
															}
															/{ totalStepsCount }
														</span>
													</span>

													<span
														className="absolute h-full top-0 left-0 bg-[#BAE6FD]/40 transition-[width] duration-300 ease-in-out"
														style={ {
															width: `${
																( completedStepsCount /
																	totalStepsCount ) *
																100
															}%`,
														} }
													/>
												</>
											}
											variant={ getBadgeColor() }
										/>
									</div>
								</Accordion.Trigger>
								<Accordion.Content className="overflow-visible [&>div]:p-0">
									{ item?.description && (
										<Text
											className="px-3 sm:px-4 ml-7 mr-0 sm:mx-8 -mt-2 pb-4 max-w-full sm:max-w-[72%]"
											size={ 14 }
											color="secondary"
											weight={ 400 }
										>
											{ item?.description }
										</Text>
									) }

									<span className="block w-full h-[0.5px] bg-border-subtle" />

									<div className="px-3 sm:px-4 bg-gs-secondary flex flex-col">
										{ item?.steps?.map( ( step, index ) => (
											<ActionItemStep
												key={ step.id }
												step={ step }
												actionItem={ item }
												isLast={
													index ===
													item.steps.length - 1
												}
											/>
										) ) }
									</div>
								</Accordion.Content>
							</Accordion.Item>
						);
					} ) }
				</Accordion>
			) }
		</div>
	);
};

export default ActionItems;
